import React, { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { fmt, parseVariants, itemLabel, stockOf, TILE_COLORS } from "../uiUtils";
import { inflatePrice } from "../engine/dualPrice";

// POS-style grid (InStoreProductsListAdapter): every product AND every variant is its own equal
// tile, flattened. Tap adds; long-press asks the server for the live quantity.
//
// The tile carries a colour spine and a large, tabular price. Price is the second thing read after
// the name, so it gets real size — at a counter these are read at arm's length, not leaned into.

// Dual price: tiles show the CARD price (Android stores the inflated price in its product table).
function buildCells(products, dualPrice) {
  const cells = [];
  for (const p of products) {
    const variants = p.__fromSearch
      ? parseVariants(p).filter((v) => v.variantId && v.variantId !== "0")
      : parseVariants(p);
    if (p.__fromSearch && String(p.isvarient) === "1" && variants.length === 0) continue; // every variant id-less
    if (variants.length === 0) {
      const st = stockOf(p, null);
      cells.push({ key: `p-${p.id}`, label: itemLabel(p, null), price: inflatePrice(dualPrice, Number(p.price) || 0), product: p, variant: null, oos: st.tracked && st.qty <= 0 });
    } else {
      for (const v of variants) {
        const st = stockOf(p, v);
        cells.push({ key: `p-${p.id}-v-${v.variantId || v.name}`, label: itemLabel(p, v), price: inflatePrice(dualPrice, v.price), product: p, variant: v, oos: st.tracked && st.qty <= 0 });
      }
    }
  }
  return cells;
}

export default function ProductGrid({ products, loading, onAdd, onLongPress }) {
  const dualPrice = useSelector((s) => s.webRegisterCart && s.webRegisterCart.dualPrice);
  const cells = useMemo(() => buildCells(products || [], dualPrice), [products, dualPrice]);
  const lp = useRef({ timer: null, fired: false });

  if (loading) return <div className="qv-empty">Loading…</div>;
  if (!cells.length) return <div className="qv-empty">No products</div>;

  const startPress = (cell) => {
    lp.current.fired = false;
    clearTimeout(lp.current.timer);
    lp.current.timer = setTimeout(() => {
      lp.current.fired = true;
      onLongPress && onLongPress(cell);
    }, 500);
  };
  const cancelPress = () => clearTimeout(lp.current.timer);
  const handleClick = (cell) => {
    if (lp.current.fired) { lp.current.fired = false; return; } // swallow the click that ends a long-press
    onAdd(cell.product, cell.variant);
  };

  return (
    <div className="qv-prods">
      {cells.map((c, i) => (
        <button
          key={c.key}
          type="button"
          className={c.oos ? "qv-tile is-oos" : "qv-tile"}
          style={{ "--spine": c.oos ? "#F97316" : TILE_COLORS[i % TILE_COLORS.length] }}
          onClick={() => handleClick(c)}
          onPointerDown={() => startPress(c)}
          onPointerUp={cancelPress}
          onPointerLeave={cancelPress}
          onPointerCancel={cancelPress}
          onContextMenu={(e) => { e.preventDefault(); onLongPress && onLongPress(c); }}
          title={c.label}
        >
          <span className="qv-tile-name">{c.label}</span>
          <span className="qv-tile-foot">
            <span className="qv-tile-price qv-num">{fmt(c.price)}</span>
            {c.oos && <span className="qv-oos">Out of stock</span>}
          </span>
        </button>
      ))}
    </div>
  );
}
